(() => {
  if (customElements.get('cc-customer-videos')) return;

  class CustomerVideos extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.grid = this.querySelector('.cc-video-grid');
      this.cards = [...this.grid.querySelectorAll('.cc-video-card')];
      this.playback = this.cards.map(() => ({ userPaused: false, manualPlay: false }));
      // A full set on either side keeps native swipes continuous across the seam.
      this.cards.forEach((card, index) => { card.dataset.videoIndex = index; });
      for (const side of ['before', 'after']) {
        const copies = this.cards.map(card => {
          const copy = card.cloneNode(true);
          copy.dataset.videoClone = side;
          copy.removeAttribute('data-shopify-editor-block');
          copy.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
          copy.inert = true;
          copy.setAttribute('aria-hidden', 'true');
          return copy;
        });
        if (side === 'before') this.grid.prepend(...copies);
        else this.grid.append(...copies);
      }
      this.clips = [...this.querySelectorAll('.cc-video-frame')].flatMap(frame => {
        const video = frame.querySelector('video');
        const button = frame.querySelector('.cc-video-toggle');
        if (!video || !button) return [];
        const card = frame.closest('.cc-video-card');
        const state = this.playback[Number(card.dataset.videoIndex)];
        const clip = { video, button, frame, card, state, visible: false };
        const update = () => {
          button.dataset.paused = String(video.paused);
          button.setAttribute('aria-label', `${video.paused ? 'Play' : 'Pause'} video: ${button.dataset.title}`);
        };
        video.muted = true;
        video.controls = false;
        button.hidden = false;
        video.addEventListener('play', update, options);
        video.addEventListener('pause', update, options);
        video.addEventListener('error', () => {
          video.controls = true;
          button.hidden = true;
        }, options);
        button.addEventListener('click', () => {
          state.userPaused = !video.paused;
          state.manualPlay = video.paused;
          this.clips.filter(item => item.state === state).forEach(item => this.syncClip(item));
        }, options);
        update();
        return [clip];
      });

      this.observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          const clip = this.clips.find(item => item.frame === entry.target);
          if (!clip) continue;
          clip.visible = entry.isIntersecting && entry.intersectionRatio >= .15;
          this.syncClip(clip);
        }
      }, { threshold: [0, .15] });
      this.clips.forEach(clip => this.observer.observe(clip.frame));
      document.addEventListener('visibilitychange', () => this.clips.forEach(clip => this.syncClip(clip)), options);
      this.motion.addEventListener('change', () => {
        this.playback.forEach(state => { state.manualPlay = false; });
        this.clips.forEach(clip => this.syncClip(clip));
      }, options);

      const nav = this.querySelector('.cc-video-nav');
      const previous = this.querySelector('[data-direction="-1"]');
      const next = this.querySelector('[data-direction="1"]');
      const layout = () => {
        const width = this.grid.clientWidth;
        if (!width || width === this.width) return;
        const count = this.cards.length;
        const oldIndex = this.step ? (this.grid.scrollLeft - (this.looping ? this.cycle : 0)) / this.step : 0;
        const index = count ? ((Math.round(oldIndex) % count) + count) % count : 0;
        this.width = width;
        const gap = parseFloat(getComputedStyle(this.grid).columnGap) || 0;
        this.step = (this.cards[0]?.getBoundingClientRect().width || 0) + gap;
        this.cycle = this.step * count;
        this.looping = count > 1 && this.cycle - gap > width + 2;
        this.grid.classList.toggle('is-looping', this.looping);
        nav.hidden = !this.looping;
        previous.disabled = next.disabled = !this.looping;
        this.grid.scrollTo({ left: this.looping ? this.cycle + index * this.step : 0, behavior: 'instant' });
        this.syncAccessibleCards();
      };
      [previous, next].forEach(button => button.addEventListener('click', () => {
        if (!this.looping) return;
        this.normalizeScroll();
        const index = Math.round(this.grid.scrollLeft / this.step) + Number(button.dataset.direction);
        this.grid.scrollTo({ left: index * this.step, behavior: this.motion.matches ? 'instant' : 'smooth' });
      }, options));
      const settle = () => {
        clearTimeout(this.scrollTimer);
        if (!this.pointerDown) this.normalizeScroll();
      };
      this.grid.addEventListener('pointerdown', () => { this.pointerDown = true; }, options);
      const release = () => {
        this.pointerDown = false;
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(settle, 160);
      };
      document.addEventListener('pointerup', release, options);
      document.addEventListener('pointercancel', release, options);
      this.grid.addEventListener('scrollend', settle, options);
      this.grid.addEventListener('scroll', () => {
        this.syncAccessibleCards();
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(settle, 160);
      }, { ...options, passive: true });
      this.resizeObserver = new ResizeObserver(layout);
      this.resizeObserver.observe(this.grid);
      layout();
    }

    syncAccessibleCards() {
      const bounds = this.grid.getBoundingClientRect();
      this.grid.querySelectorAll('.cc-video-card').forEach(card => {
        const rect = card.getBoundingClientRect();
        const visible = (!card.dataset.videoClone || this.looping) && Math.min(rect.right, bounds.right) - Math.max(rect.left, bounds.left) > 3;
        card.inert = !visible;
        card.setAttribute('aria-hidden', String(!visible));
      });
    }

    normalizeScroll() {
      if (!this.looping) return;
      const left = this.grid.scrollLeft;
      if (left >= this.cycle - 1 && left < 2 * this.cycle - 1) return;
      // Match the currently visible copy's playback before moving to the identical set.
      for (const source of this.clips.filter(clip => clip.visible)) {
        for (const target of this.clips.filter(clip => clip.state === source.state && clip !== source)) {
          if (target.video.readyState > 0) target.video.currentTime = source.video.currentTime;
        }
      }
      const normalized = ((left % this.cycle) + this.cycle) % this.cycle + this.cycle;
      this.grid.scrollTo({ left: normalized, behavior: 'instant' });
    }

    shouldPlay(clip) {
      return this.isConnected && !document.hidden && clip.visible && !clip.state.userPaused && (!this.motion.matches || clip.state.manualPlay);
    }

    syncClip(clip) {
      if (!this.shouldPlay(clip)) {
        clip.video.pause();
        return;
      }
      // Speed is already encoded in the uploaded clips; do not apply it a second time.
      clip.video.play().then(() => {
        if (!this.shouldPlay(clip)) clip.video.pause();
      }).catch(() => {
        clip.button.dataset.paused = 'true';
        clip.button.setAttribute('aria-label', `Play video: ${clip.button.dataset.title}`);
      });
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      clearTimeout(this.scrollTimer);
      this.clips?.forEach(clip => clip.video.pause());
      this.grid?.querySelectorAll('[data-video-clone]').forEach(card => card.remove());
      this.cards?.forEach(card => { card.inert = false; card.removeAttribute('aria-hidden'); });
      this.width = 0;
      this.abort = null;
    }
  }

  customElements.define('cc-customer-videos', CustomerVideos);
})();
